"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/custom/ui/card";
import { supabase } from "@/supabase/client";
import jwt from "jsonwebtoken";
import { ethers } from "ethers";
import { rewardAbi } from "@/components/abi/nodes";
import { toast } from "react-toastify";
import {
  CheckCircle2,
  XCircle,
  Sparkles,
  Zap,
  Target,
  Trophy,
} from "lucide-react";
import ShortUniqueId from "short-unique-id";
import { detect } from "detect-browser";

const CONTRACT_ADDRESS_FOR_REWARD_CLAIM =
  process.env.NEXT_PUBLIC_CONTRACT_ADDRESS_FOR_SEASON_REWARDS;

// Add a test wallets array at the top of the component
const TEST_WALLETS = [
  // Add lowercase wallet addresses here for testing
  "0x3bce4e756fc6f5b30f7cdda4512f953c2154d377",
  "0x5b0c27bf6828551124557ac8b5c501f605d9faca",
  "0x52c316a27265aa56558558792a1c947b8ac82c7f",
  "0xa20fb60bad7437a286fec46d06fac847a3194474",
  "0x5bc6005b3a757ce84b26ed3610b76a908f3261d3",
  "0x5b0c27bf6828551124557ac8b5c501f605d9faca",
  "0x6ee2a9100f62e2fda999e0ba6c0dee2d17a44fca",
];

const SEASON_REWARD_OVERRIDES = {
  "0x62eeab28e3910086fc148687838c02a060fca82d": {
    overclaim: 4975694.28,
    airdropRewardsOwed: 10018587.14,
    finalReward: 5042892.86,
  },
  "0x8069cccbad493dcc166ba92b29cc37eb60007e97": {
    overclaim: 2115469.16,
    airdropRewardsOwed: 4731638.67,
    finalReward: 2616169.51,
  },
};

const getSeasonRewardAdjustment = (walletAddress) => {
  if (!walletAddress) {
    return null;
  }

  return SEASON_REWARD_OVERRIDES[walletAddress.toLowerCase()] || null;
};

const CriteriaSection = ({
  selectedSeasonData,
  airdropCriterias,
  currentUserResult,
  loadCriteria,
  walletAddress,
  user, // <-- add user prop
  activeSeasonData,
  previousSeasonData,
}) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [userResult, setUserResult] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [claimStatusLoading, setClaimStatusLoading] = useState(false);

  React.useEffect(() => {
    // console.log(selectedSeasonData);
    if (!selectedSeasonData) {
      setCriteria(null);
      return;
    }
    // Calculate if criteria should be revealed
    const shouldReveal =
      selectedSeasonData.is_reveal_criteria ||
      (walletAddress && TEST_WALLETS.includes(walletAddress.toLowerCase()));

    // Only fetch criteria if criteria should be revealed
    if (!shouldReveal) {
      setCriteria(null);
      return;
    }
    setFetching(true);
    const fetchCriteria = async () => {
      const { data, error } = await supabase
        .from("seasons_airdrop_criterias")
        .select("*")
        .eq("season_id", selectedSeasonData.id)
        .limit(1)
        .maybeSingle();
      setCriteria(error ? null : data);
      setFetching(false);
    };
    fetchCriteria();
  }, [selectedSeasonData, walletAddress]);

  React.useEffect(() => {
    if (!selectedSeasonData || !walletAddress) {
      setUserResult(null);
      return;
    }
    // Calculate if criteria should be revealed
    const shouldReveal =
      selectedSeasonData.is_reveal_criteria ||
      (walletAddress && TEST_WALLETS.includes(walletAddress.toLowerCase()));

    // Only fetch user result if criteria should be revealed
    if (!shouldReveal) {
      setUserResult(null);
      return;
    }
    setUserLoading(true);
    const fetchUserResult = async () => {
      const { data, error } = await supabase
        .from(`seasons_airdrop_results_${selectedSeasonData.id}`)
        .select("*")
        .eq("wallet_lc", walletAddress.toLowerCase())
        .maybeSingle();
      setUserResult(error ? null : data);
      setUserLoading(false);
    };
    fetchUserResult();
  }, [selectedSeasonData, walletAddress]);

  const rewardAdjustment = getSeasonRewardAdjustment(walletAddress);
  const totalAirdroppedAmount =
    rewardAdjustment?.airdropRewardsOwed ?? userResult?.reward ?? null;
  const overclaimAmount = rewardAdjustment?.overclaim ?? 0;
  const finalRewardAmount =
    rewardAdjustment?.finalReward ?? userResult?.reward ?? null;

  React.useEffect(() => {
    if (!walletAddress || !selectedSeasonData?.id) return;
    // Calculate if criteria should be revealed
    const shouldReveal =
      selectedSeasonData.is_reveal_criteria ||
      (walletAddress && TEST_WALLETS.includes(walletAddress.toLowerCase()));

    // Only fetch claim status if criteria should be revealed
    if (!shouldReveal) {
      setAlreadyClaimed(false);
      return;
    }
    const fetchClaimStatus = async () => {
      setClaimStatusLoading(true);
      try {
        console.log("[fetchClaimStatus] Checking claim status for:", {
          walletAddress: walletAddress.toLowerCase(),
          seasonId: selectedSeasonData.id,
          seasonName: selectedSeasonData.name,
          fullSeasonData: selectedSeasonData,
        });

        const { data, error } = await supabase
          .from("seasons_reward_claim_history")
          .select("*")
          .eq("wallet_address_lc", walletAddress.toLowerCase())
          .eq("season_id", selectedSeasonData.id)
          .eq("status", "claimed")
          .maybeSingle();

        console.log("[fetchClaimStatus] Query result:", { data, error });
        setAlreadyClaimed(data && data.status === "claimed");
      } catch (err) {
        console.error("[fetchClaimStatus] Error:", err);
        setAlreadyClaimed(false);
      } finally {
        setClaimStatusLoading(false);
      }
    };
    fetchClaimStatus();
  }, [walletAddress, selectedSeasonData]);

  // Helper to get user IP address
  const getIpAddress = async () => {
    try {
      console.log("[sys_audit] Fetching user IP address...");
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      // console.log("[sys_audit] IP address fetched:", data.ip);
      return data.ip;
    } catch (err) {
      console.error("[sys_audit] Failed to fetch IP address:", err);
      return "unknown";
    }
  };

  const suid = new ShortUniqueId({ length: 28 });

  // Helper to log to sys_audit
  const logSysAudit = async ({
    uid,
    email,
    ip_address,
    event = "XSEED",
    sub_event = "REWARD",
    type = "SEASON",
    game_name = "ECOSYSTEM",
    success,
    comment,
    target = "",
  }) => {
    try {
      const id = suid.rnd();
      // console.log("[sys_audit] Generated id:", id);
      // console.log("[sys_audit] Inserting audit log:", {
      //   id,
      //   uid,
      //   email,
      //   ip_address,
      //   event,
      //   sub_event,
      //   type,
      //   game_name,
      //   success,
      //   comment,
      //   target,
      // });
      const { data, error } = await supabase.from("sys_audit").insert([
        {
          id,
          uid,
          email,
          ip_address,
          event,
          sub_event,
          type,
          game_name,
          success,
          comment,
          target,
        },
      ]);
      if (error) {
        console.error("[sys_audit] Error inserting audit log:", error);
        throw error;
      }
      // console.log("[sys_audit] Audit log inserted successfully:", data);
      return data;
    } catch (err) {
      console.error("[sys_audit] Failed to insert audit log:", err);
      throw err;
    }
  };

  // Helper to store claim attempt in seasons_reward_claim_attempt
  async function storeSeasonClaimAttempt({
    user,
    rewardsResponse,
    claimableAmount,
    wallet,
  }) {
    try {
      // Detect browser details
      const browser = detect();
      const browserName = browser?.name || "Unknown";
      const browserVersion = browser?.version || "Unknown";

      // Fetch additional browser information
      const {
        platform,
        userAgent,
        language,
        deviceMemory,
        hardwareConcurrency,
      } = navigator;
      const cores = hardwareConcurrency || "Unknown";
      const memory = deviceMemory ? `${deviceMemory} GB` : "Unknown";

      // Helper function to fetch IP address
      const getIPAddress = async () => {
        try {
          const response = await fetch("https://api.ipify.org?format=json");
          const { ip } = await response.json();
          return ip;
        } catch (error) {
          console.error("Failed to fetch IP address:", error.message);
          return "Unknown";
        }
      };

      // Helper function to fetch geolocation details
      const getGeoLocation = async (ip) => {
        try {
          const response = await fetch(`https://ipapi.co/${ip}/json/`);
          return await response.json();
        } catch (error) {
          console.error("Failed to fetch geo details:", error.message);
          return {};
        }
      };

      // Fetch IP address and geo details
      const ipAddress = await getIPAddress();
      const geoDetails = await getGeoLocation(ipAddress);

      // Prepare claim details for Supabase
      const claimDetails = {
        wallet,
        wallet_lc: wallet.toLowerCase(),
        uid: user.user_id,
        ip_address: ipAddress,
        city: geoDetails.city,
        region: geoDetails.region,
        region_code: geoDetails.region_code,
        country_code: geoDetails.country_code,
        country_code_iso3: geoDetails.country_code_iso3,
        country_name: geoDetails.country_name,
        country_capital: geoDetails.country_capital,
        country_tld: geoDetails.country_tld,
        continent_code: geoDetails.continent_code,
        in_eu: geoDetails.in_eu,
        postal: geoDetails.postal,
        lat: geoDetails.latitude,
        lon: geoDetails.longitude,
        timezone: geoDetails.timezone,
        utc_offset: geoDetails.utc_offset,
        country_calling_code: geoDetails.country_calling_code,
        currency: geoDetails.currency,
        currency_name: geoDetails.currency_name,
        languages: geoDetails.languages,
        country_area: geoDetails.country_area,
        country_population: geoDetails.country_population,
        asn: geoDetails.asn,
        org: geoDetails.org,
        user_claim_amount: rewardsResponse?.amountInBigNumber
          ? ethers.utils.formatEther(rewardsResponse.amountInBigNumber)
          : null,
        claimable_amount: claimableAmount,
        last_claim_date: new Date().toISOString(),
        created_at: new Date().toISOString(),
        browser_name: browserName,
        browser_version: browserVersion,
        platform: platform || "Unknown",
        user_agent: userAgent || "Unknown",
        language: language || "Unknown",
        cores,
        memory,
      };

      // Insert data into `seasons_reward_claim_attempt` in Supabase
      const { error } = await supabase
        .from("seasons_reward_claim_attempt")
        .insert([claimDetails]);

      if (error) {
        console.error("Failed to store claim attempt:", error.message);
        throw new Error("Could not store claim attempt in database.");
      }
      // console.log(
      //   "[claim_attempt] Claim attempt stored successfully:",
      //   claimDetails
      // );
    } catch (error) {
      console.error("Error in storing claim attempt:", error.message);
      throw error;
    }
  }

  const handleClaimSeasonReward = async () => {
    let nonce, signature;
    let claimSuccess = false;
    let claimError = null;
    let rewardsResponse = null;
    try {
      setClaimLoading(true);
      const token = generateToken(walletAddress, {
        amount: finalRewardAmount,
        season_id: selectedSeasonData.id,
      });
      if (!token) throw new Error("Token validation failed.");

      const response = await fetch(
        // "http://127.0.0.1:5001/privatemxs/us-central1/generateSeasonRewardSignature",
      "https://us-central1-privatemxs.cloudfunctions.net/generateSeasonRewardSignature",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const res = await response.json();
      ({ nonce, signature } = res);
      const { amount, expiry } = res;
      const amountInBigNumber = ethers.utils.parseEther(amount.toString());
      if (typeof window === "undefined") {
        throw new Error("This function must be run in the browser.");
      }
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const rewardContract = new ethers.Contract(
        CONTRACT_ADDRESS_FOR_REWARD_CLAIM,
        rewardAbi,
        signer
      );
      rewardsResponse = await rewardContract.claimReward(
        amountInBigNumber._hex,
        nonce,
        expiry,
        signature
      );
      const receipt = await rewardsResponse.wait();
      if (receipt.status === 1) {
        await updateClaimStatus(nonce, signature, "claimed");
        setAlreadyClaimed(true);
        toast.success("🎉 Claim successful!");
        claimSuccess = true;
        // Store claim attempt for seasons
        try {
          await storeSeasonClaimAttempt({
            user, 
            rewardsResponse, 
            claimableAmount: finalRewardAmount,
            wallet: walletAddress,
          });
        } catch (claimAttemptErr) {
          console.error(
            "[claim_attempt] Failed to store claim attempt:",
            claimAttemptErr
          );
        }
      } else {
        await updateClaimStatus(nonce, signature, "claim_failed");
        toast.error("❌ Claim failed!");
        throw new Error("Transaction failed");
      }
    } catch (error) {
      claimError = error;
      if (nonce && signature) {
        await updateClaimStatus(nonce, signature, "claim_failed");
      }
      toast.error("❌ Claim failed!");
    } finally {
      setClaimLoading(false);
      // --- AUDIT LOG ---
      try {
        console.log("[sys_audit] Starting audit log...");
        const ip_address = await getIpAddress();
        await logSysAudit({
          uid: user?.user_id,
          email: user?.email,
          ip_address,
          success: claimSuccess,
          comment: claimSuccess
            ? "User successfully claimed XSEED season reward."
            : `Claim failed: ${claimError?.message || "Unknown error"}`,
          target: walletAddress,
        });
        console.log("[sys_audit] Audit log complete.");
      } catch (auditErr) {
        console.error("[sys_audit] Audit log failed:", auditErr);
        toast.error("Audit log failed. Please contact support.");
      }
    }
  };

  const updateClaimStatus = async (nonce, signature, status) => {
    try {
      const { data, error } = await supabase
        .from("seasons_reward_claim_history")
        .update({ status })
        .eq("nonce", nonce)
        .eq("signature", signature)
        .select();
      if (error)
        throw new Error(error.message || "Failed to update claim status");
      return true;
    } catch {
      return false;
    }
  };

  const cards = [
    {
      title: "Mystery Criterion 1",
      icon: Sparkles,
      description: "Unlock this criteria to discover more",
    },
    {
      title: "Mystery Criterion 2",
      icon: Zap,
      description: "Complete challenges to reveal",
    },
    {
      title: "Mystery Criterion 3",
      icon: Target,
      description: "Hidden requirements await",
    },
    {
      title: "Mystery Criterion 4",
      icon: Trophy,
      description: "The final mystery to solve",
    },
  ];

  // Helper to determine if criteria should be revealed
  const shouldRevealCriteria = (() => {
    if (!selectedSeasonData) return false;
    // Always reveal for test wallets
    if (walletAddress && TEST_WALLETS.includes(walletAddress.toLowerCase()))
      return true;
    // Reveal for everyone if is_reveal_criteria is true
    if (selectedSeasonData.is_reveal_criteria) return true;
    // Otherwise, do not reveal
    return false;
  })();

  // Helper to determine if we should show the mystery cards (when criteria not revealed)
  const shouldShowMysteryCards = (() => {
    if (!selectedSeasonData) return false;
    // Show mystery cards if is_reveal_criteria is false and not a test wallet
    if (
      !selectedSeasonData.is_reveal_criteria &&
      !(walletAddress && TEST_WALLETS.includes(walletAddress.toLowerCase()))
    ) {
      return true;
    }
    return false;
  })();

  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-2xl font-bold text-white">
          Airdrop Criteria Status{" "}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {/* Show criteria and user performance if allowed */}
        {shouldRevealCriteria && criteria && !fetching
          ? // Filter the important fields from criteria
            (() => {
              const filteredCriteriaEntries = Object.entries(criteria).filter(
                ([key, value]) =>
                  typeof value === "number" &&
                  value !== 0 &&
                  !["id", "season_id", "created_at", "updated_at"].includes(key)
              );
              // Get the relevant user result fields
              const filteredUserResult = userResult
                ? Object.fromEntries(
                    filteredCriteriaEntries.map(([key]) => [
                      key,
                      userResult[key],
                    ])
                  )
                : {};
              // Map filtered criteria to cards by index
              return filteredCriteriaEntries.map(([key, value], idx) => {
                const card = cards[idx] || cards[0];
                const Icon = card.icon;
                const required = Number(value);
                const userValue = Number(filteredUserResult[key] || 0);
                const passed = userValue >= required && required > 0;
                return (
                  <Card
                    key={key}
                    className="bg-[#181f2a] border border-[#232c3a] rounded-2xl shadow-none flex flex-col items-center justify-center min-h-[220px]"
                    style={{ boxShadow: "none" }}
                  >
                    <div className="flex flex-col items-center w-full h-full p-0">
                      <div className="flex items-center justify-center mt-6 mb-3">
                        <div className="rounded-lg bg-[#232c3a] p-3 flex items-center justify-center">
                          <Icon
                            className="h-8 w-8 text-[#b0b8c9]"
                            strokeWidth={1.5}
                          />
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1 mt-1">
                        {card.title}
                      </h3>
                      <div className="text-xs text-[#b0b8c9] mb-2 tracking-wide uppercase">
                        {key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </div>
                      <div className="text-3xl font-extrabold text-white mb-1">
                        {userLoading ? "..." : userValue.toLocaleString()}{" "}
                        <span className="text-lg font-normal text-[#b0b8c9]">
                          / {required}
                        </span>
                      </div>
                      <div className="flex items-center justify-center mt-1 mb-4">
                        {required > 0 ? (
                          passed ? (
                            <span className="text-green-400 flex items-center gap-1 text-base font-semibold">
                              <CheckCircle2 className="w-5 h-5" /> Passed
                            </span>
                          ) : (
                            <span className="text-red-400 flex items-center gap-1 text-base font-semibold">
                              <XCircle className="w-5 h-5" /> Not Passed
                            </span>
                          )
                        ) : null}
                      </div>
                    </div>
                  </Card>
                );
              });
            })()
          : criteria === null || !walletAddress
          ? // Show mystery cards if season ended and not revealed, using the provided design
            cards.map((card, index) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  onHoverStart={() => setHoveredCard(index)}
                  onHoverEnd={() => setHoveredCard(null)}
                >
                  <Card
                    className="w-full h-48 bg-black rounded-lg border-2 overflow-hidden relative group cursor-pointer"
                    style={{ borderColor: "#333" }}
                  >
                    <div className="absolute inset-0 bg-black opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-white to-gray-300"></div>

                    <CardContent className="flex flex-col items-center justify-center h-full w-full p-6 text-center">
                      <div className="mb-3 p-2 rounded-full bg-black/50 border border-zinc-800 group-hover:border-white/50 transition-colors duration-300">
                        <Icon
                          className={`h-8 w-8 ${
                            hoveredCard === index
                              ? "text-white"
                              : "text-gray-300/80"
                          } transition-all duration-300`}
                          strokeWidth={1.5}
                        />
                      </div>
                      <h3
                        className="text-xl bg-gradient-to-b from-white to-gray-300 bg-clip-text text-transparent font-bold mb-2"
                        style={{
                          WebkitBackgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {card.title}
                      </h3>
                      <p className="text-zinc-400 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {card.description}
                      </p>

                      <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-16 h-1 rounded-full bg-gradient-to-r from-white/0 via-white/50 to-white/0 group-hover:w-24 transition-all duration-300"></div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          : null}
      </div>
      {/* Only show the reward/congrats and next season cards if shouldShowMysteryCards is false */}
      {!shouldShowMysteryCards &&
        shouldRevealCriteria &&
        userResult &&
        criteria &&
        !userLoading && (
          <div className="mt-4 flex flex-col items-center justify-center w-full">
            {userResult.status === "passed" ? (
              <div className="w-full flex flex-col md:flex-row gap-6 justify-center items-stretch">
                {/* Left: Congratulations Card */}
                <div className="flex-1 min-w-[320px] max-w-xl rounded-2xl border border-green-900 bg-gradient-to-br from-[#1a2a1e] to-[#151a1a] p-8 flex flex-col items-center justify-center shadow-lg text-center">
                  <div className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
                    Congratulations!{" "}
                    <span role="img" aria-label="party">
                      🎉
                    </span>
                  </div>
                  <div className="text-base text-zinc-300 mb-6">
                    You've met all the airdrop criteria and are eligible to
                    claim your rewards!
                  </div>
                  <div className="w-full bg-[#232c3a] rounded-xl flex flex-col md:flex-row items-center justify-between p-6 mb-6 gap-6">
                    <div className="flex-1 flex flex-col items-center md:items-start">
                      <div className="flex items-center gap-2 text-yellow-400 font-semibold mb-1 text-sm">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                          />
                          <path
                            d="M12 8v4l3 3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Total Airdropped
                      </div>
                      <div className="text-xl md:text-2xl font-extrabold text-white">
                        {totalAirdroppedAmount
                          ? totalAirdroppedAmount.toLocaleString()
                          : "-"}{" "}
                        XSEED
                      </div>
                      {rewardAdjustment && (
                        <div className="mt-3 text-xs text-zinc-300 leading-relaxed max-w-sm">
                          This wallet was overclaimed earlier, so the total airdropped amount is shown separately from the final reward.
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col items-center md:items-end">
                      <div className="flex items-center gap-2 text-blue-300 font-semibold mb-1 text-sm">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M3 17l6-6 4 4 8-8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Final Reward
                      </div>
                      <div className="text-xl md:text-2xl font-extrabold text-white">
                        {finalRewardAmount
                          ? finalRewardAmount.toLocaleString()
                          : "-"}{" "}
                        XSEED
                      </div>
                      {rewardAdjustment && overclaimAmount > 0 && (
                        <div className="mt-3 text-xs text-zinc-300 leading-relaxed text-right">
                          Remaining credits after subtracting overclaim:{" "}
                          <span className="font-semibold text-white">
                            {finalRewardAmount
                              ? finalRewardAmount.toLocaleString()
                              : "-"}
                          </span>{" "}
                          XSEED
                        </div>
                      )}
                    </div>
                  </div>
                  {(() => {
                    if (alreadyClaimed) {
                      return (
                        <div className="text-green-400 font-bold text-lg mt-4">
                          Reward already claimed for this season.
                        </div>
                      );
                    } else {
                      return (
                        <button
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg flex items-center gap-2 text-base mx-auto"
                          onClick={handleClaimSeasonReward}
                          disabled={claimStatusLoading || claimLoading}
                        >
                          {claimLoading ? (
                            <>
                              <svg
                                className="animate-spin w-5 h-5 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8z"
                                ></path>
                              </svg>
                              Claiming...
                            </>
                          ) : claimStatusLoading ? (
                            <>
                              <svg
                                className="animate-spin w-5 h-5 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8z"
                                ></path>
                              </svg>
                              Checking status...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  d="M12 8v4l3 3"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  fill="none"
                                />
                              </svg>
                              Claim Your Rewards
                            </>
                          )}
                        </button>
                      );
                    }
                  })()}
                </div>
                {/* Right: Previous and active season card */}
                <div className="flex-1 min-w-[320px] max-w-xl rounded-2xl border border-blue-900 bg-gradient-to-br from-[#18213a] to-[#151a12] p-8 flex flex-col items-center justify-center shadow-lg text-center">
                 <div className="text-2xl font-bold text-white mb-1">
                    {previousSeasonData
                      ? `Season ${previousSeasonData.number ?? previousSeasonData.season_number ?? previousSeasonData.id} Has Ended!`
                      : "Previous Season Has Ended!"}
                  </div>
                  <div className="text-blue-400 font-semibold mb-2 text-base">
                    {activeSeasonData
                      ? `Season ${activeSeasonData.number ?? activeSeasonData.season_number ?? activeSeasonData.id} is Active`
                      : "No Active Season"}
                  </div>
                  <div className="text-base text-zinc-300 mb-4">
                    {previousSeasonData
                      ? `Season ${previousSeasonData.number ?? previousSeasonData.season_number ?? previousSeasonData.id} has concluded! Thank you to all participants. `
                      : "Thank you to all participants. "}
                    {activeSeasonData
                      ? `Season ${activeSeasonData.number ?? activeSeasonData.season_number ?? activeSeasonData.id} is now active with new missions and exciting rewards. Complete tasks and be ready to earn more rewards!`
                      : "The next season will be announced soon."}
                  </div>
                  <div className="flex gap-4 mt-2 justify-center">
                    <div className="flex items-center gap-1 text-blue-300 bg-blue-900/40 px-3 py-1 rounded-full text-sm font-medium">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v7m0 0h7m-7 0H5"
                        />
                      </svg>
                      Active rewards
                    </div>
                    <div className="flex items-center gap-1 text-blue-300 bg-blue-900/40 px-3 py-1 rounded-full text-sm font-medium">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 17v-2a4 4 0 018 0v2m-4-6a4 4 0 100-8 4 4 0 000 8zm0 0v2m0 4v2"
                        />
                      </svg>
                      Track progress
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full flex flex-col md:flex-row gap-6 justify-center items-stretch">
                {/* Left: Failed Card */}
                <div className="flex-1 min-w-[320px] max-w-xl rounded-2xl border border-red-900 bg-gradient-to-br from-[#2a1a1e] to-[#1a151a] p-8 flex flex-col items-center justify-center shadow-lg text-center">
                  <div className="text-2xl font-bold text-white mb-1">
                    Season{" "}
                    {selectedSeasonData?.season_number ||
                      selectedSeasonData?.id ||
                      "1"}{" "}
                    Complete
                  </div>
                  <div className="text-red-400 font-semibold mb-2">
                    Requirements not fully met
                  </div>
                  <div className="text-zinc-300">
                    You completed{" "}
                    {
                      Object.entries(criteria).filter(
                        ([key, value]) =>
                          typeof value === "number" &&
                          value !== 0 &&
                          ![
                            "id",
                            "season_id",
                            "created_at",
                            "updated_at",
                          ].includes(key) &&
                          Number(userResult[key] || 0) >= Number(value)
                      ).length
                    }{" "}
                    out of{" "}
                    {
                      Object.entries(criteria).filter(
                        ([key, value]) =>
                          typeof value === "number" &&
                          value !== 0 &&
                          ![
                            "id",
                            "season_id",
                            "created_at",
                            "updated_at",
                          ].includes(key)
                      ).length
                    }{" "}
                    criteria. The season has ended.
                  </div>
                </div>
                {/* Right: Season 2 Opens Soon Card */}
                <div className="flex-1 min-w-[320px] max-w-xl rounded-2xl border border-blue-900 bg-gradient-to-br from-[#18213a] to-[#151a2a] p-8 flex flex-col items-center justify-center shadow-lg text-center">
                  <div className="text-2xl font-bold text-white mb-1">
                    Season 2 Opens Soon!
                  </div>
                  <div className="text-blue-400 font-semibold mb-2">
                    Fresh start, new opportunities
                  </div>
                  <div className="text-zinc-300 mb-4">
                    Don't worry! Season 2 will begin soon with new criteria and
                    another chance to earn rewards. Every season is a fresh
                    opportunity to participate and qualify.
                  </div>
                  <div className="flex gap-4 mt-2 justify-center">
                    <div className="flex items-center gap-1 text-blue-300 bg-blue-900/40 px-3 py-1 rounded-full text-sm font-medium">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3zm0 0V4m0 7v7m0 0h7m-7 0H5"
                        />
                      </svg>
                      New reward pool
                    </div>
                    <div className="flex items-center gap-1 text-blue-300 bg-blue-900/40 px-3 py-1 rounded-full text-sm font-medium">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 17v-2a4 4 0 018 0v2m-4-6a4 4 0 100-8 4 4 0 000 8zm0 0v2m0 4v2"
                        />
                      </svg>
                      Fresh criteria
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default CriteriaSection;

const generateToken = (address, payload = {}) => {
  const secret = process.env.NEXT_PUBLIC_JWT_SECRET;
  const expiresIn = "1h";
  if (!address) throw new Error("Wallet address is required");
  if (!secret) throw new Error("Secret key is required");
  const basePayload = { walletAddress: address };
  const tokenPayload = { ...basePayload, ...payload };
  try {
    return jwt.sign(tokenPayload, secret, { expiresIn });
  } catch {
    throw new Error("Failed to generate JWT token");
  }
};
